/**
 * What a waiting session asks, ready for the drawer's card (ADR 0056): the level (blocked or
 * asked), the message above its closing question, the question itself, quick replies, and why
 * nothing can be shown when that is the case. Pure, so it is tested directly.
 */
import type { AskLevel, Session } from '@orgus/protocol';
import { askFromText, askLevel, sessionAskOf } from '@orgus/protocol';

export interface AskView {
  level: AskLevel | null;
  /** The message above its closing question, Markdown ('' when there is none). */
  body: string;
  /** The closing question, shown on its own and emphasised; null when the text does not end with one. */
  question: string | null;
  /** Choices the message lists, for quick replies. */
  options: string[];
  askedAt: number | null;
  /**
   * Why Orgus cannot show what it asked: 'content-off' (the workspace keeps transcript text on
   * the machine) or 'unreadable' (no message reached Orgus).
   */
  missing: 'content-off' | 'unreadable' | null;
}

const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** `text` without its closing `question` (matched across line breaks), or null when it does not end with it. */
export function withoutQuestion(text: string, question: string): string | null {
  const words = question.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return null;
  const m = new RegExp(`${words.map(escape).join('\\s+')}\\s*$`).exec(text);
  return m ? text.slice(0, m.index).trimEnd() : null;
}

export function askViewOf(s: Pick<Session, 'status' | 'ext' | 'lastText'>, held: boolean): AskView {
  const level = askLevel(s, held);
  const ask = sessionAskOf(s.ext);
  const text = (ask ? ask.text : s.lastText).trim();
  const missing = text ? null : ask ? 'content-off' : 'unreadable';
  const found = ask ? { question: ask.question, options: ask.options } : askFromText(text);
  const rest = found.question ? withoutQuestion(text, found.question) : null;
  return {
    level,
    body: rest ?? text,
    question: rest !== null ? found.question : null,
    options: found.options,
    askedAt: ask?.askedAt || null,
    missing,
  };
}
